import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, CalendarCheck } from "lucide-react";
import { QJ_CATEGORIES } from "../lib/qj";

const links = [
  { to: "/", label: "Home" },
  { to: "/over", label: "Over ons" },
  { to: "/diensten", label: "Diensten" },
  { to: "/motoren", label: "Tweedehands" },
  { to: "/afspraak", label: "Afspraak" },
  { to: "/santini", label: "Santini" },
  { to: "/qj-motor", label: "QJ Motor" },
  { to: "/contact", label: "Contact" },
];
const tid = (l) => `nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`;

function DesktopLink({ l }) {
  return (
    <NavLink to={l.to} end={l.to === "/"} data-testid={tid(l)}
      className={({ isActive }) => `font-display uppercase text-sm font-600 tracking-wide px-3 py-2 transition-colors ${isActive ? "text-[#E10600]" : "text-[#F2F2F3] hover:text-[#E10600]"}`}>
      {l.label}
    </NavLink>
  );
}

const mCls = ({ isActive }) =>
  `flex-1 flex items-center min-h-[52px] font-display uppercase text-lg font-700 tracking-wide ${isActive ? "text-[#E10600]" : "text-[#F2F2F3]"}`;

function MobileQJ() {
  const { pathname } = useLocation();
  const [sub, setSub] = useState(pathname.startsWith("/qj-motor"));
  return (
    <li className="border-b border-[#1C1D22]">
      <div className="flex items-center">
        <NavLink to="/qj-motor" data-testid="m-nav-link-qj-motor" className={mCls}>QJ Motor</NavLink>
        <button type="button" onClick={() => setSub(!sub)} aria-expanded={sub} aria-controls="m-qj-sub" data-testid="m-nav-qj-toggle"
          aria-label="Toon QJ Motor-categorieën" className="w-12 h-12 flex items-center justify-center text-[#9A9CA3]">
          <ChevronDown size={22} className={`transition-transform duration-200 ${sub ? "rotate-180" : ""}`} />
        </button>
      </div>
      {sub && (
        <ul id="m-qj-sub" className="grid grid-cols-2 gap-2 pb-4">
          {QJ_CATEGORIES.map((c) => (
            <li key={c.slug}>
              <NavLink to={`/qj-motor/categorie/${c.slug}`} data-testid={`m-nav-qj-${c.slug}`}
                className={({ isActive }) => `flex items-center min-h-[44px] px-3 bg-[#141518] border font-display uppercase text-sm font-600 tracking-wide ${isActive ? "border-[#E10600] text-white" : "border-[#2A2B31] text-[#B8B9BE]"}`}>
                {c.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function MobileMenu() {
  return (
    <div data-testid="nav-mobile-menu" className="lg:hidden bg-[#0A0A0B] border-t border-[#2A2B31] px-5 pt-2 pb-6 overflow-y-auto overscroll-contain"
      style={{ maxHeight: "calc(100dvh - 5rem - 60px)" }}>
      <nav aria-label="Mobiel menu">
        <ul>
          {links.map((l) => l.to === "/qj-motor" ? <MobileQJ key={l.to} /> : (
            <li key={l.to} className="border-b border-[#1C1D22] flex">
              <NavLink to={l.to} end={l.to === "/"} data-testid={`m-${tid(l)}`} className={mCls}>{l.label}</NavLink>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-2 gap-2 mt-5">
          <a href="tel:0485741524" data-testid="m-nav-phone" className="flex items-center justify-center gap-2 min-h-[52px] border border-[#2A2B31] text-white font-display uppercase font-700">
            <Phone size={17} className="text-[#E10600]" /> Bellen
          </a>
          <Link to="/afspraak" data-testid="m-nav-afspraak" className="flex items-center justify-center gap-2 min-h-[52px] bg-[#E10600] text-white font-display uppercase font-700">
            <CalendarCheck size={17} /> Afspraak
          </Link>
        </div>
      </nav>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || open ? "bg-[#0A0A0B]/95 backdrop-blur-xl border-b border-[#2A2B31]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0" aria-label="TT Motorsports — naar home">
          <img src="/tt-logo.png" alt="TT Motorsports" className="h-11 w-auto" width="90" height="44" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Hoofdmenu">
          {links.map((l) => <DesktopLink key={l.to} l={l} />)}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a href="tel:0485741524" data-testid="nav-phone"
            className="hidden xl:flex items-center gap-2 text-sm font-mono-tt text-[#9A9CA3] hover:text-[#F2F2F3]">
            <Phone size={15} className="text-[#E10600]" /> 0485 74 15 24
          </a>
          <Link to="/afspraak" data-testid="nav-cta-afspraak"
            className="bg-[#E10600] hover:bg-[#B80500] text-white font-display uppercase font-700 text-sm tracking-wide px-5 py-2.5 transition-colors">
            Maak afspraak
          </Link>
        </div>

        <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}
          className="lg:hidden text-[#F2F2F3] w-12 h-12 -mr-2 flex items-center justify-center"
          aria-label={open ? "Menu sluiten" : "Menu openen"} aria-expanded={open}>
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && <MobileMenu />}
    </header>
  );
}
